import { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';
import { useCurrency } from '../../context/CurrencyContext';
import { apiGetGeo, apiGetCountries } from '../../api/geo';

const STORAGE_KEY = 'bookscape_country_v2';
const CACHE_KEY = 'bookscape_countries_cache_v3';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const SELECTABLE_COUNTRY_CODES = new Set(['IN', 'US']);
const normalizeCountries = (countries = []) =>
  countries
    .map((country) => ({
      ...country,
      code: String(country?.code || '').toUpperCase(),
      currencyCode: String(country?.currencyCode || '').toUpperCase(),
    }))
    .filter(
      (country) =>
        SELECTABLE_COUNTRY_CODES.has(country.code) &&
        /^[A-Z]{3}$/.test(country.currencyCode),
    );

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.value || !parsed?.savedAt) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return normalizeCountries(parsed.value);
  } catch {
    return null;
  }
};

const writeCache = (value) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    // ignore
  }
};

const findByCountry = (countries, code) => countries.find((c) => c.code === code);
const resolveStorefrontCountry = (countries, geo) => {
  const detectedCode = String(geo?.detectedCountry || geo?.country || '').toUpperCase();
  const storefrontCode = detectedCode && detectedCode !== 'IN' ? 'US' : 'IN';
  return findByCountry(countries, storefrontCode) || countries[0];
};
const FlagImg = ({ code, size = 20, className = '' }) => (
  <img
    src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
    width={size}
    height={Math.round(size * 0.75)}
    alt={code}
    className={`inline-block rounded-sm object-cover ${className}`}
    loading="lazy"
  />
);

const CountrySelector = () => {
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState(() => readCache() || []);
  const [selected, setSelected] = useState(null);
  const initialised = useRef(false);
  const pendingAddressCountry = useRef('');
  const ref = useRef(null);
  const { setCurrency } = useCurrency();

  // Load supported countries from the backend (cached 24h locally).
  useEffect(() => {
    let cancelled = false;
    apiGetCountries()
      .then((data) => {
        if (cancelled || !Array.isArray(data?.countries)) return;
        const supported = normalizeCountries(data.countries);
        setCountries(supported);
        writeCache(supported);
      })
      .catch(() => {
        // Network failed — use cache or empty list
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve initial selection once countries are loaded.
  useEffect(() => {
    if (initialised.current || countries.length === 0) return;
    initialised.current = true;

    // Migrate legacy storage key
    try {
      localStorage.removeItem('bookscape_country');
    } catch (_) {} // eslint-disable-line no-empty

    const saved = localStorage.getItem(STORAGE_KEY);
    const addressCountry = pendingAddressCountry.current
      ? findByCountry(countries, pendingAddressCountry.current)
      : null;
    if (addressCountry) {
      setSelected(addressCountry);
      setCurrency(addressCountry.currencyCode);
      return;
    }
    const match = saved ? findByCountry(countries, saved) : null;
    if (match) {
      setSelected(match);
      setCurrency(match.currencyCode);
      return;
    }

    apiGetGeo()
      .then((geo) => {
        const initial = resolveStorefrontCountry(countries, geo);
        if (initial) {
          setSelected(initial);
          setCurrency(initial.currencyCode);
        }
      })
      .catch(() => {
        const firstConfiguredCountry = countries[0];
        if (firstConfiguredCountry) {
          setSelected(firstConfiguredCountry);
          setCurrency(firstConfiguredCountry.currencyCode);
        }
      });
  }, [countries, setCurrency]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Checkout address selection determines the storefront market. Map all
  // non-India destinations to the supported international (USD) storefront.
  useEffect(() => {
    const handleAddressCountryChange = (event) => {
      const code = String(event.detail?.code || '').toUpperCase();
      pendingAddressCountry.current = code;
      const country = findByCountry(countries, code);
      if (!country) return;
      setSelected(country);
      setCurrency(country.currencyCode);
    };
    window.addEventListener('bookscape:address-country-change', handleAddressCountryChange);
    return () => window.removeEventListener('bookscape:address-country-change', handleAddressCountryChange);
  }, [countries, setCurrency]);

  // Keep every country-dependent feature (shipping, currency, etc.) in sync,
  // including the initial geo-detected selection—not only manual changes.
  useEffect(() => {
    if (!selected?.code) return;
    try {
      localStorage.setItem(STORAGE_KEY, selected.code);
    } catch {
      // ignore
    }
    window.dispatchEvent(
      new CustomEvent('bookscape:country-change', { detail: selected }),
    );
  }, [selected]);

  const handleSelect = (country) => {
    if (!countries.some(({ code }) => code === country?.code)) return;
    setSelected(country);
    setCurrency(country.currencyCode);
    setOpen(false);
  };

  if (!selected) {
    // Render a quiet placeholder until data is ready — no flash of wrong UI.
    return (
      <div
        className="flex items-center gap-1.5 bg-gray-900 text-white rounded-full pl-1 pr-2 py-1 border border-gray-700 shadow-sm opacity-60"
        aria-hidden
      >
        <span className="w-5 h-5 rounded-full bg-gray-700 inline-block" />
        <span className="text-xs font-semibold tracking-wide">…</span>
      </div>
    );
  }

  return (
    <div className="relative zindex" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Selected country: ${selected.name}`}
        className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-full pl-1 pr-2 py-1 border border-gray-700 transition shadow-sm"
      >
        <span className="w-5 h-5 inline-flex items-center justify-center">
          <FlagImg code={selected.code} size={20} />
        </span>
        <span className="text-xs font-semibold tracking-wide">{selected.currencyCode}</span>
        <FiChevronDown
          className={`text-xs text-orange-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 bg-white text-black rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 max-h-80 overflow-y-auto"
        >
          {countries.map((c) => {
            const active = c.code === selected.code;
            return (
              <button
                key={c.code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => handleSelect(c)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-orange-50 transition ${
                  active ? 'bg-orange-50' : ''
                }`}
              >
                <span className="w-6 inline-flex items-center justify-center flex-shrink-0">
                  <FlagImg code={c.code} size={24} />
                </span>
                <span className="flex-1">
                  <span className="block font-medium text-sm text-black">{c.name}</span>
                  <span className="block text-[11px] text-black">
                    {c.language ? `${c.language.toUpperCase()} · ` : ''}{c.currencyCode}
                  </span>
                </span>
                {active && <FiCheck className="text-orange-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
